// PollWriteService.js

/**
 * PollWriteService.js
 *
 * Central place for Firestore poll vote writes.
 */

// Firebase
import { doc, setDoc } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Services
import { nowTimestamp } from 'services/nodes/NodeWriteService';

// Helper to check for non-empty string
const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

/**
 * Casts a vote for a specific poll option by a user.
 * 
 * @param {Object} params - Parameters for casting the vote.
 * @param {string} params.pollNodeKey - The key of the poll node.
 * @param {string} params.userKey - The key of the user casting the vote.
 * @param {number} params.optionIndex - The index of the chosen poll option.
 * 
 * @returns {Promise<void>} Resolves when the vote is recorded.
 */
export async function castPollVote({ pollNodeKey, userKey, optionIndex }) {

	// Validate inputs
	if (!isNonEmptyString(pollNodeKey)) {
		throw new Error('castPollVote: pollNodeKey is required');
	}
	if (!isNonEmptyString(userKey)) {
		throw new Error('castPollVote: userKey is required');
	}

	// Validate optionIndex
	const index = Number(optionIndex);
	if (!Number.isFinite(index) || index < 0) {
		throw new Error('castPollVote: optionIndex must be a non-negative number');
	}

	// Reference to the vote document
	const voteRef = doc(db, collections.nodes, pollNodeKey, 'votes', userKey);

	// Write the vote
	await setDoc(voteRef, {
		nodeKey: pollNodeKey,
		optionIndex: index,
		votedAt: nowTimestamp(),
	});
}
